import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import {
  fetchCompanyContacts,
  type CompanyContactQueryParams,
} from '@/api/v4/organization-contacts.api';
import { fetchSpotlightCompanyContacts } from '@/api/v4/spotlight.api';
import { QueryKey } from '@/config/query-client';

import { companyContactsPageState, companyIdState } from '../company.state';
import {
  type CompanyFetchMode,
  useCompanyFetchMode,
} from '../company-fetch-context';

const companyContactsFetchers: Record<
  CompanyFetchMode,
  (
    companyId: string,
    params: CompanyContactQueryParams,
  ) => ReturnType<typeof fetchCompanyContacts>
> = {
  general: fetchCompanyContacts,
  spotlight: fetchSpotlightCompanyContacts,
};

export const useCompanyContactsQuery = () => {
  const companyId = useAtomValue(companyIdState);
  const { page, pageSize } = useAtomValue(companyContactsPageState);
  const fetchMode = useCompanyFetchMode();

  return useQuery({
    queryKey: [QueryKey.CompanyContacts, fetchMode, companyId, page, pageSize],
    queryFn: async () => {
      if (!companyId) {
        return {
          contacts: [],
          lastEditedAt: null,
          poweredBy: null,
        };
      }

      const response = await companyContactsFetchers[fetchMode](companyId, {
        items: pageSize,
        page,
      });

      return {
        ...response,
        contacts: response.contacts.map(contact => ({
          ...contact,
          synced: true,
        })),
      };
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 5,
  });
};
